import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense, type ComponentType } from 'react';

// ── AppLayout MUST be a direct (non-lazy) import ──────────────────────────────
// Layout components that render <Outlet /> cannot be lazy-loaded in React Router v6.
import { AppLayout } from '../components/layout/AppLayout';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { RouteErrorBoundary } from '../components/common/RouteErrorBoundary';
import { useAuthStore } from '../store/auth.store';
import type { UserRole } from '../types';

// ── Pages — lazy loaded via default exports ───────────────────────────────────
const LoginPage         = lazy(() => import('../pages/auth/LoginPage'));
const SecuritySetupPage = lazy(() => import('../pages/auth/SecuritySetupPage'));
const DashboardPage     = lazy(() => import('../pages/dashboard/DashboardPage'));
const ProvidersPage      = lazy(() => import('../pages/providers/ProvidersPage'));
const ProviderDetailPage = lazy(() => import('../pages/providers/ProviderDetailPage'));
const AppsPage          = lazy(() => import('../pages/apps/AppsPage'));
const CreateAppPage     = lazy(() => import('../pages/apps/CreateAppPage'));
const AppDetailPage     = lazy(() => import('../pages/apps/AppDetailPage'));
const VersionDetailPage = lazy(() => import('../pages/versions/VersionDetailPage'));
const TeamPage          = lazy(() => import('../pages/developer/TeamPage'));

const VersionReviewPage = lazy(() => import('../pages/versions/VersionReviewPage'));
const PendingReviewPage = lazy(() => import('../pages/review/PendingReviewPage'));
const ReleasesPage      = lazy(() => import('../pages/releases/ReleasesPage'));
const DatasourcesPage   = lazy(() => import('../pages/datasources/DatasourcesPage'));
const PermissionsPage   = lazy(() => import('../pages/permissions/PermissionsPage'));
const CapabilitiesPage  = lazy(() => import('../pages/capabilities/CapabilitiesPage'));
const IntentsPage       = lazy(() => import('../pages/intents/IntentsPage'));
const CategoriesPage    = lazy(() => import('../pages/categories/CategoriesPage'));
const AuditLogsPage     = lazy(() => import('../pages/audit/AuditLogsPage'));
const DeviceBindingsPage = lazy(() => import('../pages/device-bindings/DeviceBindingsPage'));
const AdminsPage        = lazy(() => import('../pages/admin/AdminsPage'));
const AdminAppsPage     = lazy(() => import('../pages/admin/AdminAppsPage'));
const AdminAppDetailPage = lazy(() => import('../pages/admin/AdminAppDetailPage'));

// ── Loading fallback ──────────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '60vh', color: 'var(--text-secondary)', fontSize: 14,
      fontFamily: 'Geist Mono, monospace', letterSpacing: '0.04em',
    }}>
      Loading...
    </div>
  );
}

// Public route helper (redirects to home if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { accessToken, user } = useAuthStore();
  if (accessToken && user) {
    return <Navigate to={user.role === 'partner_dev' ? '/apps' : '/dashboard'} replace />;
  }
  return <>{children}</>;
}

// Protected route helper
function S({ Page, allowedRoles }: { Page: ComponentType; allowedRoles?: UserRole[] }) {
  return (
    <ProtectedRoute allowedRoles={allowedRoles}>
      <Suspense fallback={<PageLoader />}>
        <Page />
      </Suspense>
    </ProtectedRoute>
  );
}

// ── Smart Index Redirect based on role ─────────────────────────────────────────
function IndexRedirect() {
  const { user } = useAuthStore();
  if (user?.role === 'partner_dev') {
    return <Navigate to="/apps" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

// ── Router ────────────────────────────────────────────────────────────────────
export const router = createBrowserRouter([
  {
    path: '/login',
    errorElement: <RouteErrorBoundary />,
    element: (
      <PublicRoute>
        <Suspense fallback={<PageLoader />}>
          <LoginPage />
        </Suspense>
      </PublicRoute>
    ),
  },

  {
    path: '/security-setup',
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<PageLoader />}>
        <SecuritySetupPage />
      </Suspense>
    ),
  },

  {
    path: '/',
    errorElement: <RouteErrorBoundary />,
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true,                                    element: <IndexRedirect /> },

      // Admin Only Domains
      { path: 'dashboard',                              element: <S Page={DashboardPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'providers',                               element: <S Page={ProvidersPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'providers/:id',                           element: <S Page={ProviderDetailPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'admin/apps',                             element: <S Page={AdminAppsPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'admin/apps/:id',                         element: <S Page={AdminAppDetailPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'admins',                                  element: <S Page={AdminsPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'review',                                 element: <S Page={PendingReviewPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'releases',                               element: <S Page={ReleasesPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'permissions',                            element: <S Page={PermissionsPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'datasources',                            element: <S Page={DatasourcesPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'audit-logs',                             element: <S Page={AuditLogsPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'device-bindings',                        element: <S Page={DeviceBindingsPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'apps/:appId/versions/:versionId/review', element: <S Page={VersionReviewPage} allowedRoles={['platform_admin', 'admin']} /> },

      // Developer Workspace Domains
      { path: 'apps',                                   element: <S Page={AppsPage} allowedRoles={['partner_dev']} /> },
      { path: 'apps/create',                            element: <S Page={CreateAppPage} allowedRoles={['partner_dev']} /> },
      { path: 'apps/new',                               element: <S Page={CreateAppPage} allowedRoles={['partner_dev']} /> },
      { path: 'apps/:appId',                            element: <S Page={AppDetailPage} allowedRoles={['partner_dev']} /> },
      { path: 'apps/:appId/versions/:versionId',        element: <S Page={VersionDetailPage} allowedRoles={['partner_dev']} /> },
      { path: 'team',                                   element: <S Page={TeamPage} allowedRoles={['partner_dev']} /> },

      // Platform Reference (Admin)
      { path: 'capabilities',                           element: <S Page={CapabilitiesPage} allowedRoles={['platform_admin', 'admin']} /> },
      { path: 'categories',                             element: <S Page={CategoriesPage} /> },
      { path: 'intents',                                element: <S Page={IntentsPage} allowedRoles={['platform_admin', 'admin']} /> },

      { path: '*',                                      element: <IndexRedirect /> },
    ],
  },
]);
